/**
 * Written directions -> route waypoints.
 *
 * "Follow the alley, turn right onto the street, third exit of the roundabout,
 * then left into the back alley" is how a person describes a route, and how an
 * AI will write one. This module turns that into the waypoint polyline the
 * delivery path needs, by intersecting road centrelines and arcing around
 * roundabouts.
 *
 * It also does the thing authors forget: on a two-way road the route follows
 * the RIGHT-HAND LANE, not the centreline, so oncoming traffic has somewhere
 * to be. One-way roads run down the middle.
 *
 * The editor expands directions into explicit waypoints when it saves, so a
 * finished map never depends on this resolver reproducing the same answer
 * twice. Hand-written maps may keep the directions and resolve at load.
 */

import type { Vec2 } from "../../core/vec";
import { ROAD_WIDTH } from "./registry";
import {
  isRing,
  type BandRoadDoc,
  type DirectionDoc,
  type MapDoc,
  type RingRoadDoc,
  type RoadDoc,
  type RouteWaypointDoc,
  type XZ,
} from "./types";

export class RouteResolveError extends Error {
  constructor(
    message: string,
    readonly at?: string,
  ) {
    super(at ? `${at}: ${message}` : message);
    this.name = "RouteResolveError";
  }
}

/** Degrees of arc between sampled points around a roundabout. */
const RING_STEP_DEG = 10;

export function resolveDirections(doc: MapDoc): RouteWaypointDoc[] {
  const steps = doc.route.directions ?? [];
  if (steps.length === 0) {
    throw new RouteResolveError("no waypoints and no directions — a map needs one or the other", "route");
  }
  const byId = new Map<string, RoadDoc>(doc.roads.map((r) => [r.id, r]));
  const out: RouteWaypointDoc[] = [];

  /** How many times the route has turned onto each road, for anchor names. */
  const turnsOnto = new Map<string, number>();
  let road: BandRoadDoc | null = null;
  let pos: Vec2 | null = null;
  /** Unit travel direction along the current road. */
  let dir: Vec2 | null = null;

  const push = (p: Vec2, extra: Partial<RouteWaypointDoc> = {}): void => {
    out.push({ at: [round(p.x), round(p.z)], ...extra });
  };

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]!;
    const where = `route.directions[${i}]`;

    if ("follow" in step) {
      const r = band(byId, step.follow, where);
      const poly = polyline(r);
      if (road === null) {
        // Opening move: start at whichever end is farther from where we go
        // next, so the route runs the length of the road rather than off it.
        const target = nextTargetPoint(doc, byId, steps, i + 1);
        const startIdx = target && dist(poly[0]!, target) < dist(poly[poly.length - 1]!, target)
          ? poly.length - 1
          : 0;
        const start = doc.route.start ? placeXZ(doc.route.start.at, where) : poly[startIdx]!;
        const towards = startIdx === 0 ? poly[1]! : poly[poly.length - 2]!;
        dir = unit(sub(towards, start));
        pos = start;
        road = r;
        push(offsetForLane(start, dir, r), { road: r.id, name: out.length === 0 ? "start" : undefined });
      } else {
        road = r;
      }
      continue;
    }

    if ("turn" in step) {
      if (!road || !pos || !dir) throw new RouteResolveError("turn before any road was followed", where);
      const onto = band(byId, step.onto, where);
      const hit = intersectAhead(polyline(road), polyline(onto), pos, dir);
      if (!hit) {
        throw new RouteResolveError(
          `road "${road.id}" and "${onto.id}" do not cross ahead of the route`,
          where,
        );
      }
      // Leave along the new road, in whichever sense matches the turn.
      const ontoDir = turnDirection(polyline(onto), hit.point, dir, step.turn);
      // Scale the corner to the NARROWER road. A fillet sized off the wide
      // street bulges across a 3.6 m alley and puts the robot on the far kerb,
      // which the playtest catches as a wedge just after the turn.
      const fillet = Math.max(1.5, Math.min(widthOf(onto), widthOf(road)) / 2);
      // Turn where the two LANES meet, not where the centrelines cross.
      // Approaching in the right-hand lane of a wide street and aiming at
      // the centreline intersection makes the route swing left before it
      // turns right — which puts the robot on the far kerb of a narrow
      // alley. The lane lines are what the robot is actually driving on.
      for (const bend of bendsBetween(polyline(road), pos, hit.point, road)) {
        push(bend.at, { road: road.id, fillet: bend.fillet });
      }
      const anchor = anchorName(step.onto, bump(turnsOnto, step.onto));
      if (step.turn === "left") {
        const hooked = hookLeftTurn(push, road, onto, hit.point, dir, ontoDir, anchor);
        if (hooked) {
          road = onto;
          pos = hooked.pos;
          dir = ontoDir;
          continue;
        }
      }
      const corner =
        lineIntersect(offsetForLane(hit.point, dir, road), dir, offsetForLane(hit.point, ontoDir, onto), ontoDir) ??
        offsetForLane(hit.point, ontoDir, onto);
      push(corner, {
        road: onto.id,
        fillet,
        name: anchor,
      });
      road = onto;
      pos = hit.point;
      dir = ontoDir;
      continue;
    }

    if ("roundabout" in step) {
      if (!road || !pos || !dir) throw new RouteResolveError("roundabout before any road was followed", where);
      const ring = ringRoad(byId, step.roundabout, where);
      const onto = band(byId, step.onto, where);
      const center: Vec2 = { x: ring.center[0], z: ring.center[1] };
      const laneR = (ring.innerR + ring.outerR) / 2;
      const ringEntry = ringPoint(center, ring.outerR, ringAngleOf(polyline(road), center, ring.outerR, pos, where));
      for (const bend of bendsBetween(polyline(road), pos, ringEntry, road)) {
        push(bend.at, { road: road.id, fillet: bend.fillet });
      }
      const psiIn = ringAngleOf(polyline(road), center, ring.outerR, pos, where);
      const psiOut = ringAngleOf(polyline(onto), center, ring.outerR, null, where);
      const sense: 1 | -1 = ring.clockwise ? 1 : -1;

      // Generous fillets at both ring joins: with the lane painted ~3 m right
      // of the driving line, a 3 m corner put the band's inner edge at zero
      // radius (a cusp fan); 4.5 m leaves it a real curve.
      // Join and leave on CHORDS: the ring is entered ~30° further round (in the
      // circulating sense) and left ~30° early, so the straight legs from and
      // to the roads meet the circulating lane nearly tangentially — the way
      // the district's hand-built joins do. Entering radially hinged the
      // route ~85° inside a single station: the robot jerked round it and no
      // ribbon could paint it without a fan.
      const CHORD = (30 * Math.PI) / 180;
      const psiJoin = psiIn + sense * CHORD;
      const psiLeave = psiOut - sense * CHORD;
      push(ringPoint(center, laneR, psiJoin), { road: ring.id, fillet: 4.5, name: "r_in" });
      for (const psi of arcAngles(psiJoin, psiLeave, sense)) {
        push(ringPoint(center, laneR, psi), { road: ring.id });
      }
      // The exit point gets the same fillet as the entry (and as the
      // district's hand-written r_out): without one the route hinged ~100°
      // in a single station here — a cusp the robot had to jerk around and
      // that no ribbon can paint without folding.
      const exitPt = ringPoint(center, laneR, psiLeave);
      push(exitPt, { road: ring.id, fillet: 4.5, name: "r_out" });

      const ontoDir = awayFrom(polyline(onto), exitPt, center);
      const leave = ringPoint(center, ring.outerR + widthOf(onto) / 4, psiOut);
      push(offsetForLane(leave, ontoDir, onto), { road: onto.id, fillet: 3 });
      road = onto;
      pos = leave;
      dir = ontoDir;
      continue;
    }

    // arrive
    const at = placeXZ(step.arrive, where);
    if (road && pos) {
      for (const bend of bendsBetween(polyline(road), pos, at, road)) {
        push(bend.at, { road: road.id, fillet: bend.fillet });
      }
    }
    push(at, { name: "dest", road: road?.id });
  }

  if (out.length < 2) {
    throw new RouteResolveError("directions produced fewer than two waypoints", "route.directions");
  }
  // Name the first and last so encounters always have anchors to hang on.
  if (!out.some((w) => w.name === "start")) out[0]!.name = "start";
  if (!out.some((w) => w.name === "dest")) out[out.length - 1]!.name = "dest";
  return out;
}

// ---------------------------------------------------------------------------
// Two-stage ("hook") left turn.

/** A left turn on a two-way road at least this wide is taken in two stages. */
const HOOK_MIN_WIDTH_M = 7;
/** How far past the junction edge the pivot/crossing line sits — right at
 * the corner, on the zebra (the renderer paints them starting 1.5 m out
 * from the arm edge). Play-testing: the turn comes IMMEDIATELY after the
 * junction, not a ride past it. */
const HOOK_SETBACK_M = 1.6;
/** Half-width given to the crossing-leg waypoints: a zebra-band, not a lane. */
const HOOK_CROSS_HALF_W = 1.6;

/**
 * The left-turn rule (play-tested): on a SMALL road, turn left like any other
 * vehicle — predictable. On a LARGE two-way road, never cut the junction like
 * a car: go straight through, stop at the far corner, turn 90° on the spot,
 * and cross the road on the zebra like a pedestrian, then merge onto the new
 * road's right-hand lane.
 *
 * Emits: P1 the pivot (named with the turn's anchor, so turn cues and
 * encounters keep working), P2 the far end of the zebra leg (named
 * `<anchor>__xend` — the compiler turns the P1..P2 pair into a crossing
 * interval the corridor and painted lane centre themselves on), then a short
 * merge onto the new lane. Falls back to the ordinary corner (returns null)
 * when the geometry does not allow going straight through — the current road
 * or the target road ends at the junction.
 */
function hookLeftTurn(
  push: (p: Vec2, extra?: Partial<RouteWaypointDoc>) => void,
  road: BandRoadDoc,
  onto: BandRoadDoc,
  hit: Vec2,
  dir: Vec2,
  ontoDir: Vec2,
  anchor: string,
): { pos: Vec2 } | null {
  const wA = widthOf(road);
  const wB = widthOf(onto);
  if (road.oneWay || wA < HOOK_MIN_WIDTH_M) return null;
  // Straight-through must be possible: the current road continues past the
  // junction, and the new road continues past the far side of this one.
  const zebraDist = wB / 2 + HOOK_SETBACK_M;
  const p1c = { x: hit.x + dir.x * zebraDist, z: hit.z + dir.z * zebraDist };
  if (!polylineReaches(polyline(road), { x: p1c.x + dir.x * 2, z: p1c.z + dir.z * 2 })) return null;
  // A longer merge run reads as "then go straight": the short one ended with
  // a visible right-hand correction onto the lane.
  const mergeC = { x: hit.x + ontoDir.x * (wA / 2 + 6), z: hit.z + ontoDir.z * (wA / 2 + 6) };
  if (!polylineReaches(polyline(onto), mergeC)) return null;

  const left = { x: -dir.z, z: dir.x };
  // P1: the pivot sits IN the painted kerb lane (a metre RIGHT of the
  // route's lane line): the lane paint runs dead straight into this corner
  // and the crossing band starts where the lane ends — one square L on the
  // road. The walking-pace band makes the tight arc trackable, and the
  // pivot keeps ~0.6 m of swing room to the kerb.
  // The pivot sits 1.5 m right of the route's lane line — inside the painted
  // kerb lane, with ~0.6 m of swing room to the kerb — and turns on a 2 m arc:
  // ONE smooth left turn, not a square hinge (play-testing: "one nice turn
  // left", no sudden turn or ugly geometry).
  const p1raw = offsetForLane(p1c, dir, road);
  const p1 = { x: p1raw.x - left.x * 1.5, z: p1raw.z - left.z * 1.5 };
  push(p1, { road: road.id, fillet: 2.0, halfWidth: 1.8, name: anchor });
  // P2: the perpendicular crossing on the zebra line, to 1.5 m past the
  // centreline. From here the line SWOOPS onto the new road in one gentle
  // curve — a two-corner descent (west, south, west) read as going in and
  // out. Geometry forces the descent to begin before the far kerb: any line
  // that reaches the far side at zebra height must then clear the junction
  // corner, which a smooth curve cannot.
  const crossLen = wA / 4 + 3.0;
  const p2 = { x: p1.x + left.x * crossLen, z: p1.z + left.z * crossLen };
  push(p2, { road: road.id, fillet: 3.0, halfWidth: HOOK_CROSS_HALF_W });
  // The swoop's through-point: just inside the far side of the junction,
  // a metre clear of the corner curb, with the same generous fillet.
  // …landing 1.2 m inside the kerb: close enough that the crossing band
  // joins the edge-line paint with only a hand's width of easing, far enough
  // that the chassis does not brush the kerb (landing on the lane's exact
  // centre, 0.9 m in, cost the scripted runner two kerb contacts).
  const landIn = 1.2;
  const p2b = {
    x: hit.x + dir.x * (wB / 2 - landIn) + left.x * (wA / 2 - 0.2),
    z: hit.z + dir.z * (wB / 2 - landIn) + left.z * (wA / 2 - 0.2),
  };
  // The crossing interval ENDS here, where the swoop has landed beside the
  // kerb lane: from this point the kerb-relative paint takes over. Ending it
  // at the merge point instead let the route's own bend there — it leaves the
  // kerb lane for a delivery point drawn at the road centre — leak into the
  // route-centred crossing band, and the paint zig-zagged after the turn.
  push(p2b, { road: onto.id, fillet: 3.0, name: `${anchor}__xend` });
  // The swoop lands ON the painted kerb lane of the new road, not on the
  // route's lane line 2.5 m out: the crossing band is centred on the route,
  // so landing on the lane line drew the paint down to it and the kerb-hug
  // then pulled it straight back out — a zig-zag. The band's centre sits
  // 0.575 m (half its width) inside the kerb; the route continues to its
  // destination from there.
  const p3raw = offsetForLane(mergeC, ontoDir, onto);
  // The painted lane's right edge is the road-edge LINE (0.32 m inside the
  // kerb), and its centre is half a strip inside that.
  const laneCentreIn = 0.32 + 0.575;
  const toKerb = onto.oneWay ? wB / 2 - laneCentreIn : wB / 4 - laneCentreIn;
  const p3 = { x: p3raw.x + ontoDir.z * toKerb, z: p3raw.z - ontoDir.x * toKerb };
  push(p3, { road: onto.id, fillet: 3 });
  return { pos: p3 };
}

/** True when `p` sits within the band road's polyline extent (with margin). */
function polylineReaches(poly: Vec2[], p: Vec2): boolean {
  for (let i = 0; i + 1 < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[i + 1]!;
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const len2 = dx * dx + dz * dz;
    if (len2 < 1e-12) continue;
    const t = ((p.x - a.x) * dx + (p.z - a.z) * dz) / len2;
    if (t < -0.02 || t > 1.02) continue;
    const qx = a.x + t * dx;
    const qz = a.z + t * dz;
    if (Math.hypot(p.x - qx, p.z - qz) < 3) return true;
  }
  return false;
}

function band(byId: Map<string, RoadDoc>, id: string, where: string): BandRoadDoc {
  const r = byId.get(id);
  if (!r) throw new RouteResolveError(`no road with id "${id}"`, where);
  if (isRing(r)) throw new RouteResolveError(`"${id}" is a roundabout, not a road to follow`, where);
  return r;
}

function ringRoad(byId: Map<string, RoadDoc>, id: string, where: string): RingRoadDoc {
  const r = byId.get(id);
  if (!r) throw new RouteResolveError(`no road with id "${id}"`, where);
  if (!isRing(r)) throw new RouteResolveError(`"${id}" is not a roundabout`, where);
  return r;
}

function widthOf(r: BandRoadDoc): number {
  return r.width ?? ROAD_WIDTH[r.kind];
}

function polyline(r: BandRoadDoc): Vec2[] {
  return r.pts.map(([x, z]) => ({ x, z }));
}

function placeXZ(p: XZ | { s: number; d: number }, where: string): Vec2 {
  if (!Array.isArray(p)) {
    throw new RouteResolveError("route positions must be world [x, z] here", where);
  }
  return { x: p[0], z: p[1] };
}

/** Right-hand lane centre for a two-way road; the centreline for a one-way. */
/**
 * The road's own corners, between where the route is now and where it leaves.
 *
 * A `follow` step only records where the route JOINS a road; the waypoint after
 * it comes from the next step. On a straight road that is the whole geometry.
 * On a road with bends in it, it is not: without this the route runs in a
 * straight line from the join to the exit and drives across whatever is inside
 * the corner. The validator catches it ("the route is off the road at 69
 * places"), but only after the map is written.
 *
 * Each corner is offset into the lane using the direction of the segment
 * LEAVING it, which is the direction the robot will be travelling once round,
 * and given a fillet so the path rounds the corner instead of hinging on it.
 */
function bendsBetween(poly: Vec2[], from: Vec2, to: Vec2, r: BandRoadDoc): { at: Vec2; fillet: number }[] {
  const out: { at: Vec2; fillet: number }[] = [];
  const along = (p: Vec2): number => {
    // Distance along the polyline of the nearest point to p.
    let best = Infinity;
    let bestS = 0;
    let acc = 0;
    for (let i = 0; i + 1 < poly.length; i++) {
      const a = poly[i]!;
      const b = poly[i + 1]!;
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const len2 = dx * dx + dz * dz;
      const t = len2 < 1e-12 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / len2));
      const qx = a.x + t * dx;
      const qz = a.z + t * dz;
      const d2 = (p.x - qx) ** 2 + (p.z - qz) ** 2;
      if (d2 < best) {
        best = d2;
        bestS = acc + t * Math.sqrt(len2);
      }
      acc += Math.sqrt(len2);
    }
    return bestS;
  };
  const sFrom = along(from);
  const sTo = along(to);
  const lo = Math.min(sFrom, sTo);
  const hi = Math.max(sFrom, sTo);
  const forward = sTo >= sFrom;
  let acc = 0;
  let lastAcc = -Infinity;
  const corners: { at: Vec2; fillet: number }[] = [];
  for (let i = 0; i + 1 < poly.length; i++) {
    const a = poly[i]!;
    const b = poly[i + 1]!;
    acc += Math.hypot(b.x - a.x, b.z - a.z);
    if (i + 2 >= poly.length) break; // b is the far end, not a corner
    // Leave a little room either side so a corner sitting on top of the join
    // or the exit does not produce two waypoints in the same place.
    if (acc <= lo + 1 || acc >= hi - 1) continue;
    // Two corners within 4 m are polyline noise, not a dog-leg: keeping both
    // made the route (and the paint) S-wiggle across a hand-drawn road.
    if (acc - lastAcc < 4) continue;
    lastAcc = acc;
    const c = poly[i + 2]!;
    const outDir = unit(sub(c, b));
    corners.push({ at: offsetForLane(b, outDir, r), fillet: Math.max(1.5, widthOf(r) / 2) });
  }
  if (!forward) corners.reverse();
  out.push(...corners);
  return out;
}

function offsetForLane(p: Vec2, dir: Vec2, r: BandRoadDoc): Vec2 {
  if (r.oneWay) return p;
  const d = widthOf(r) / 4;
  // Right of travel, matching DeliveryPath.offsetPoint's sign convention.
  return { x: p.x + dir.z * d, z: p.z - dir.x * d };
}

/** The point the route heads for after step `i`, used to orient the opening road. */
function nextTargetPoint(
  doc: MapDoc,
  byId: Map<string, RoadDoc>,
  steps: DirectionDoc[],
  i: number,
): Vec2 | null {
  for (let k = i; k < steps.length; k++) {
    const s = steps[k]!;
    if ("turn" in s || "roundabout" in s) {
      const id = "turn" in s ? s.onto : s.roundabout;
      const r = byId.get(id);
      if (!r) continue;
      return isRing(r) ? { x: r.center[0], z: r.center[1] } : midpoint(polyline(r));
    }
    if ("arrive" in s && Array.isArray(s.arrive)) return { x: s.arrive[0], z: s.arrive[1] };
  }
  return null;
}

interface Hit {
  point: Vec2;
  /** Distance along the travel direction from the query point. */
  ahead: number;
}

/** Nearest crossing of two polylines that lies ahead of `pos` along `dir`. */
function intersectAhead(a: Vec2[], b: Vec2[], pos: Vec2, dir: Vec2): Hit | null {
  let best: Hit | null = null;
  for (let i = 0; i + 1 < a.length; i++) {
    for (let j = 0; j + 1 < b.length; j++) {
      const p = segmentIntersect(a[i]!, a[i + 1]!, b[j]!, b[j + 1]!);
      if (!p) continue;
      const ahead = dot(sub(p, pos), dir);
      if (ahead <= 0.5) continue;
      if (!best || ahead < best.ahead) best = { point: p, ahead };
    }
  }
  return best;
}

function segmentIntersect(p1: Vec2, p2: Vec2, p3: Vec2, p4: Vec2): Vec2 | null {
  const d1x = p2.x - p1.x;
  const d1z = p2.z - p1.z;
  const d2x = p4.x - p3.x;
  const d2z = p4.z - p3.z;
  const den = d1x * d2z - d1z * d2x;
  if (Math.abs(den) < 1e-9) return null; // parallel
  const t = ((p3.x - p1.x) * d2z - (p3.z - p1.z) * d2x) / den;
  const u = ((p3.x - p1.x) * d1z - (p3.z - p1.z) * d1x) / den;
  if (t < -0.02 || t > 1.02 || u < -0.02 || u > 1.02) return null;
  return { x: p1.x + d1x * t, z: p1.z + d1z * t };
}

/**
 * Which way to leave a junction so the manoeuvre reads as the named turn.
 * Left is a positive rotation from the incoming direction in this frame
 * (heading = atan2(x, z), so +x is right of +z).
 */
function turnDirection(poly: Vec2[], at: Vec2, incoming: Vec2, turn: "left" | "right"): Vec2 {
  const ends = [poly[0]!, poly[poly.length - 1]!];
  let best: Vec2 | null = null;
  let bestScore = -Infinity;
  for (const e of ends) {
    const d = unit(sub(e, at));
    if (!Number.isFinite(d.x) || !Number.isFinite(d.z)) continue;
    // cross > 0 means the new direction is to the right of the old one.
    const cross = incoming.z * d.x - incoming.x * d.z;
    const score = turn === "right" ? cross : -cross;
    if (score > bestScore) {
      bestScore = score;
      best = d;
    }
  }
  if (!best) throw new RouteResolveError("cannot work out which way that turn goes");
  return best;
}

/** Angle (psi) at which a road crosses the ring's outer curb. */
function ringAngleOf(
  poly: Vec2[],
  center: Vec2,
  rOut: number,
  from: Vec2 | null,
  where: string,
): number {
  let best: { psi: number; d: number } | null = null;
  for (let i = 0; i + 1 < poly.length; i++) {
    for (const p of circleIntersect(poly[i]!, poly[i + 1]!, center, rOut)) {
      const d = from ? dist(p, from) : 0;
      const psi = Math.atan2(p.x - center.x, p.z - center.z);
      if (!best || d < best.d) best = { psi, d };
    }
  }
  if (!best) {
    // A road that stops short of the curb still points at the ring: use the
    // bearing of its nearest end.
    const near = poly.reduce((a, b) => (dist(a, center) < dist(b, center) ? a : b));
    if (dist(near, center) > rOut * 3) {
      throw new RouteResolveError("that road does not reach the roundabout", where);
    }
    return Math.atan2(near.x - center.x, near.z - center.z);
  }
  return best.psi;
}

function circleIntersect(p1: Vec2, p2: Vec2, c: Vec2, r: number): Vec2[] {
  const dx = p2.x - p1.x;
  const dz = p2.z - p1.z;
  const fx = p1.x - c.x;
  const fz = p1.z - c.z;
  const a = dx * dx + dz * dz;
  const b = 2 * (fx * dx + fz * dz);
  const cc = fx * fx + fz * fz - r * r;
  const disc = b * b - 4 * a * cc;
  if (a < 1e-12 || disc < 0) return [];
  const sq = Math.sqrt(disc);
  const out: Vec2[] = [];
  for (const t of [(-b - sq) / (2 * a), (-b + sq) / (2 * a)]) {
    if (t >= 0 && t <= 1) out.push({ x: p1.x + dx * t, z: p1.z + dz * t });
  }
  return out;
}

/** Intermediate angles from psiIn to psiOut, travelling in `sense`. */
function arcAngles(psiIn: number, psiOut: number, sense: 1 | -1): number[] {
  const step = (RING_STEP_DEG * Math.PI) / 180;
  let sweep = (psiOut - psiIn) * sense;
  while (sweep < 0) sweep += Math.PI * 2;
  const n = Math.max(1, Math.round(sweep / step));
  const out: number[] = [];
  for (let k = 1; k < n; k++) out.push(psiIn + sense * (sweep * k) / n);
  return out;
}

/** Where two infinite lines cross, or null when they are parallel. */
function lineIntersect(p1: Vec2, d1: Vec2, p2: Vec2, d2: Vec2): Vec2 | null {
  const den = d1.x * d2.z - d1.z * d2.x;
  if (Math.abs(den) < 1e-9) return null;
  const t = ((p2.x - p1.x) * d2.z - (p2.z - p1.z) * d2.x) / den;
  return { x: p1.x + d1.x * t, z: p1.z + d1.z * t };
}

function ringPoint(center: Vec2, r: number, psi: number): Vec2 {
  return { x: center.x + Math.sin(psi) * r, z: center.z + Math.cos(psi) * r };
}

/** Direction along `poly` that leads away from the ring centre. */
function awayFrom(poly: Vec2[], at: Vec2, center: Vec2): Vec2 {
  const ends = [poly[0]!, poly[poly.length - 1]!];
  const far = dist(ends[0]!, center) > dist(ends[1]!, center) ? ends[0]! : ends[1]!;
  return unit(sub(far, at));
}

/**
 * The name a turn cue can refer to: the road turned onto, plus how many times
 * the route has turned onto THAT road.
 *
 * Deliberately not the waypoint's index. An index-based name changes whenever
 * the geometry produces a different number of waypoints — adding the road's
 * own bends to the path renamed every anchor after the first corner, and a map
 * that said "at": "s2_1" stopped compiling with "unknown path anchor". Every
 * hand-written map in the repo already assumed this counter, which is the
 * clearest evidence of what an author expects it to mean.
 */
function bump(counts: Map<string, number>, id: string): number {
  const n = (counts.get(id) ?? 0) + 1;
  counts.set(id, n);
  return n;
}

function anchorName(roadId: string, nth: number): string {
  return `${roadId.toLowerCase()}_${nth}`;
}

function midpoint(poly: Vec2[]): Vec2 {
  const a = poly[0]!;
  const b = poly[poly.length - 1]!;
  return { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 };
}

const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, z: a.z - b.z });
const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.z * b.z;
const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.z - b.z);
const round = (v: number): number => Math.round(v * 1000) / 1000;

function unit(v: Vec2): Vec2 {
  const n = Math.hypot(v.x, v.z);
  return n < 1e-9 ? { x: 0, z: 1 } : { x: v.x / n, z: v.z / n };
}
